/**
 * Copyright 2026, CS GROUP, https://www.cs-soprasteria.com/
 *
 * This file is part of AntFlow project.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import React, { useRef, useState } from 'react';
import {
  ActionCancelJob,
  Job,
  linksDefinition,
  ProcessDescription,
  useProcessesContext
} from 'ogc-processes-components';
import Tooltip from 'rc-tooltip';
import { NavLink } from 'react-router';
import { AppRoutes } from '@/constants/routes.ts';
import { Utils } from '@/utils/utils.ts';
import { CButtonRounded } from '@/components';
import { jobResultUrl } from '@/pages/JobsPage/JobDetailsPage/JobDetail/JobGeneralMetaCard';
import { useTranslation } from 'react-i18next';
import { useMutation } from '@tanstack/react-query';
import { Dialog } from 'primereact/dialog';
import { Dropdown } from 'primereact/dropdown';
import { Toast } from 'primereact/toast';
import { AntflowApiEndpoint } from '@/types/ogc-models';
import { AntFlowExecuteProcessForm } from '@/components/AntFlowExecuteProcessForm';

interface Props {
  job: Job;
  jobReference?: string;
  refreshDataCallback?: () => Promise<unknown>;
}
export const ActionBodyRender: React.FC<Props> = ({ job, jobReference, refreshDataCallback }) => {
  const { t } = useTranslation();

  const toast = useRef<Toast>(null);

  const { processesEndpoint } = useProcessesContext();

  const [projectRef, setProjectRef] = useState<string>(job.commit_ref);

  const [processRef, setProcessRef] = useState<ProcessDescription>();

  const [showDeleteAlert, setShowDeleteAlert] = useState(false);

  const [localJobState, setLocalJobSate] = useState<Job>(job);

  const { mutateAsync: getProcessDesc, isPending: processLoading } = useMutation({
    mutationKey: ['get_process_from_job'],
    mutationFn: async () => {
      const self_link = localJobState.links?.find((el) => el.rel === linksDefinition.process);
      if (self_link) {
        return processesEndpoint.get<ProcessDescription>(self_link.href);
      }
      return null;
    }
  });

  const { mutateAsync } = useMutation({
    mutationKey: ['create_job'],
    mutationFn: async (data: Record<string, unknown>) => {
      const { project_path, project_ref, processID } = localJobState;
      if (!(project_path && project_ref && processID)) {
        throw new Error(' project_path, project_ref, processID is not defined in job');
      }
      const endpoint = processesEndpoint as AntflowApiEndpoint;
      const ref = projectRef ?? project_ref;
      return endpoint.post_project_process(project_path, ref, processID, data);
    }
  });

  const jobDetailLink = (jobData: Job): string => {
    const link =
      AppRoutes.jobs.detail.url(jobData.jobID) + Utils.buildNewQueryParamsUrl({ job_reference: jobReference });
    return link;
  };

  const { mutateAsync: handleCancelJob, isPending: isStillCancel } = useMutation({
    mutationKey: ['cancel_job', localJobState],
    mutationFn: async () => {
      const _jobDefinitionLink = localJobState.links.find((link) => link.rel === linksDefinition.jobMonitor);
      if (_jobDefinitionLink) {
        const endpoint = processesEndpoint as AntflowApiEndpoint;
        endpoint.cancel_project_job(_jobDefinitionLink.href).then((res) => {
          if (res) {
            setLocalJobSate(res);
            setShowDeleteAlert(false);
          }
        });
      }
    }
  });

  return (
    <>
      <Toast ref={toast} />
      <div key={`${localJobState.jobID}`} className="flex align-items-center">
        <Tooltip overlay={t(['actions.see_more', 'see more'])} placement="top">
          <NavLink to={jobDetailLink(localJobState)}>
            <CButtonRounded className="mr-1" rounded icon="pi pi-info" />
          </NavLink>
        </Tooltip>
        {localJobState.result_url && (
          <Tooltip overlay={t(['actions.show_results', 'show_results'])} placement="top">
            <NavLink to={jobResultUrl(localJobState) ?? '#'} target="_blank">
              <CButtonRounded className="mr-1" rounded icon="pi pi-external-link" severity="contrast" />
            </NavLink>
          </Tooltip>
        )}
        {['accepted'].includes(localJobState.status) && (
          <Tooltip overlay={t(['actions.cancel', 'cancel'])} placement="top">
            <div>
              <CButtonRounded
                disabled={isStillCancel}
                onClick={() => setShowDeleteAlert(true)}
                rounded
                icon="pi pi-times"
                severity="danger"
              />
            </div>
          </Tooltip>
        )}
        {['failed', 'dismissed'].includes(localJobState.status) && (
          <Tooltip overlay={t(['actions.retry', 'retry'])} placement="top">
            <div>
              <CButtonRounded
                onClick={() => {
                  getProcessDesc().then((res) => {
                    setProcessRef(res!);
                  });
                }}
                loading={processLoading}
                rounded
                icon="pi pi-play"
                severity="success"
              />
            </div>
          </Tooltip>
        )}
      </div>

      <ActionCancelJob
        job={localJobState}
        setVisible={setShowDeleteAlert}
        handleCancelJob={handleCancelJob}
        visible={showDeleteAlert}
      />
      {processRef && (
        <Dialog
          header={processRef.title}
          visible={!!processRef}
          style={{ width: '60%', minHeight: '15rem' }}
          onHide={() => {
            setProcessRef(undefined);
          }}
        >
          <div style={{ minHeight: '25rem', height: '100%' }} className="w-full fadeinright animation-duration-100">
            <div className="mt-3 mb-4">
              <label className="font-semibold block text-sm mb-1" htmlFor="commit_ref">
                {t(['misc.project_version', 'Project version'])}
              </label>
              <Dropdown
                inputId="commit_ref"
                optionLabel="label"
                optionValue="value"
                value={projectRef}
                onChange={(e) => setProjectRef(e.value)}
                options={[
                  { label: job.commit_ref, value: job.commit_ref },
                  ...(job.commit_ref !== job.project_ref ? [{ label: job.project_ref, value: job.project_ref }] : [])
                ]}
              />
            </div>
            <AntFlowExecuteProcessForm
              className="pb-4"
              execution_content={localJobState.execution_content}
              description={processRef.description}
              inputs={processRef?.inputs}
              links={localJobState.links}
              onSubmit={({ formData }) => {
                return mutateAsync(formData).then(async (resp) => {
                  if (resp) {
                    toast.current?.show({
                      severity: 'success',
                      summary: t(['notifs.success.title'], 'Success'),
                      detail: (
                        <div className="flex flex-column">
                          <div>{t(['notifs.success.description'], 'Operation success')}</div>
                          <NavLink className="text-color" target="_blank" to={jobDetailLink(resp)}>
                            {t(['actions.see_job'], 'See more')}
                          </NavLink>
                        </div>
                      ),
                      contentClassName: 'text-left',
                      className: 'text-left',
                      life: 8000
                    });
                    setProcessRef(undefined);
                    if (refreshDataCallback) {
                      setTimeout(async () => {
                        await refreshDataCallback();
                      }, 10000);
                    }
                    return true;
                  } else {
                    toast.current?.show({
                      severity: 'error',
                      summary: t(['notifs.error.title'], 'Error'),
                      detail: t(['notifs.error.description'], 'Operation declined'),
                      contentClassName: 'text-left',
                      className: 'text-left',
                      life: 6000
                    });
                    return false;
                  }
                });
              }}
            />
          </div>
        </Dialog>
      )}
    </>
  );
};
